import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TextField,
  TablePagination,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchTodayLoanCollections } from "../../services/loanService";
import moment from "moment";

function LoanCollectionTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loanData, setLoanData] = useState([]);
  const [totalLoanCount, setTotalLoanCount] = useState(0); // Track total loans
  const [selectedChip, setSelectedChip] = useState("emi"); // Default selected chip
  const navigate = useNavigate();

  // Fetching loan collections based on pagination and search term
  const fetchData = async () => {
    // Fetch loan data from API (pagination and search term passed)
    const collections = await fetchTodayLoanCollections(page, rowsPerPage);
    setLoanData(collections.collectedLoans);
    setTotalLoanCount(collections.totalCollectedLoans); // Update total loan count for pagination
  };

  const handlePenaltyChipClick = async (type) => {
    setSelectedChip(type); // Update selected chip state
    // Fetch loan data from API (pagination and search term passed)
    if (type === "emi") {
      fetchData();
    } else if (type === "penalty") {
      const collections = await fetchTodayLoanCollections(
        page,
        rowsPerPage,
        "penalty"
      );
      setLoanData(collections.collectedLoans);
      setTotalLoanCount(collections.totalCollectedLoans); // Update total loan count for pagination
    } else {
      const collections = await fetchTodayLoanCollections(
        page,
        rowsPerPage,
        "interest"
      );
      setLoanData(collections.collectedLoans);
      setTotalLoanCount(collections.totalCollectedLoans); // Update total loan count for pagination
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchTerm]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage); // Update page state when pagination changes
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10)); // Change number of rows per page
    setPage(0); // Reset to first page
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value); // Update search term
    setPage(0); // Reset to first page when search changes
  };

  const handleCollectLoan = () => {
    navigate("/loan-collections/collect-loan");
  };
  const handlePrincipalCollectLoan = () => {
    navigate("/loan-collections/collect-principal");
  };

  // const filteredLoanData = loanData.filter(
  //   (row) =>
  //     row.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     row.loanCode.toLowerCase().includes(searchTerm.toLowerCase())
  // );

  return (
    <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          m: 2,
        }}
      >
        <Button
          variant="contained"
          size="small"
          sx={{
            py: 0.5,
            textTransform: "capitalize",
            fontSize: "16px",
            borderRadius: "8px",
          }}
          onClick={handleCollectLoan}
        >
          Collect Loan
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{
            py: 0.5,
            textTransform: "capitalize",
            fontSize: "16px",
            borderRadius: "8px",
          }}
          onClick={handlePrincipalCollectLoan}
        >
          Principal Collection
        </Button>
        <Chip
          label="EMI"
          onClick={() => handlePenaltyChipClick("emi")}
          sx={{
            backgroundColor: selectedChip === "emi" ? "#4CAF50" : "#e0e0e0", // Green for active, gray for inactive
            color: selectedChip === "emi" ? "#fff" : "#000",
            fontWeight: selectedChip === "emi" ? "bold" : "normal",
          }}
        />
        <Chip
          label="Penalty"
          onClick={() => handlePenaltyChipClick("penalty")}
          sx={{
            backgroundColor: selectedChip === "penalty" ? "#FF5722" : "#e0e0e0", // Orange for active, gray for inactive
            color: selectedChip === "penalty" ? "#fff" : "#000",
            fontWeight: selectedChip === "penalty" ? "bold" : "normal",
          }}
        />
        <Chip
          label="Interest"
          onClick={() => handlePenaltyChipClick("interest")}
          sx={{
            backgroundColor:
              selectedChip === "interest" ? "#4CAF50" : "#e0e0e0", // Green for active, gray for inactive
            color: selectedChip === "interest" ? "#fff" : "#000",
            fontWeight: selectedChip === "interest" ? "bold" : "normal",
          }}
        />
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ py: 0.5, backgroundColor: "#fff", borderRadius: 8 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}
              align="center"
            >
              SL. NO
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              LOAN CODE
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              CUSTOMER NAME
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              AGENT NAME
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              COLLECTION DATE
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              LOAN AMOUNT
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              PAID AMOUNT
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              INTEREST AMOUNT
            </TableCell>
            <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              PENALTY
            </TableCell>
            {/* <TableCell sx={{ color: "#5252ff", padding: "4px", margin: "0px" }}>
              ACTIONS
            </TableCell> */}
          </TableRow>
        </TableHead>

        <TableBody>
          {loanData?.map((row, index) => (
            <TableRow key={index}>
              <TableCell sx={{ padding: "4px", margin: "0px" }} align="center">
                {index + 1 + page * rowsPerPage}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {row?.loan?.loanCode}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {row?.loan?.user?.userName}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {row?.loan?.agent?.userName}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {moment(row?.collectionDate).format("DD-MM-YYYY")}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {row?.loan?.loanAmount?.toFixed(2)}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {row?.paidAmount?.toFixed(2)}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {row?.paidInterestAmount?.toFixed(2) || 0.0}
              </TableCell>
              <TableCell sx={{ padding: "4px", margin: "0px" }}>
                {row?.paidPenaltyAmount?.toFixed(2) || 0.0}
              </TableCell>
              {/* <TableCell sx={{ padding: "4px", margin: "0px" }}>
                <Button variant="contained" color="warning" size="small">
                  Edit
                </Button>
              </TableCell> */}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination controls */}
      <TablePagination
        component="div"
        count={totalLoanCount} // Total number of items
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </TableContainer>
  );
}

export default LoanCollectionTable;
